// @flow
import { createAction } from 'redux-actions';
import { format as formatDate, parse as parseDate } from 'date-fns';
import type { Dispatch, Thunk } from 'types/redux';
import xws from 'api/xws/';
import type { State as StoreState } from 'store/State';

// Actions
import {
  formatServices,
  formatDeclinedServices,
  setServices,
  setApptServicePoint,
  setDeclinedServices,
  setAutoRecallServices,
  setAdvisor,
  setAdvisorName,
  setStartTime,
  setTransportationOption,
  setReservationId,
  setAttendanceStatus,
  setConfirmationKey,
  setCallBack,
  setLifecycleState,
  setLeadAppt,
  setTransportationLabel,
  constructBookServiceData,
  updateTellusmoreService,
  addService,
  setTeamIds
} from 'store/reducers/appointment/actions';
import { setValetDetails } from 'store/reducers/appointment/valetDetails/actions';
import { updatePackageInfo } from 'store/reducers/packages/actions';
import actions from 'store/reducers/user/personInfo/actions';
import {
  getPromoList,
  setActivePromotion,
  getAnonymousPromotion
} from 'store/reducers/promotions/actions';
import { ajaxCallbacks } from 'store/reducers/ajaxCall/actions';
import { getTrims } from 'store/reducers/trims/actions';
import {
  setModelLabelOther,
  setMakeLabelOther
} from 'store/reducers/userInteractions/actions';

import { gtmUserEvent } from 'store/reducers/gtm/actions';

// Selectors
import { getWebkey } from 'store/reducers/queryParams/selectors';
import { getTokenId } from 'store/reducers/tokenId/selectors';
import {
  getSelfCheckInData,
  getIsDealerOpenNow
} from 'store/reducers/selfCheckIn/selectors';
import { getLocaleCode } from 'store/reducers/dealerLocale/selectors';
import {
  findMake,
  getTellUsMoreNotes,
  getTellusmoreSvcId,
  getDealerCountryCode
} from 'store/reducers/dealerSettings/selectors';
import { getStartTime } from 'store/reducers/appointment/selectors';

// Action Types
import * as actionTypes from './actionTypes';
import {
  SET_CUSTOMER_VEHICLE,
  SET_MILEAGE
} from 'store/reducers/appointment/actionTypes';

// Utils
import { customerVehicleShape } from 'utils/shape/customerVehicle';
import { formatPhoneNumber, rawPhoneNumber } from 'utils/string';

// Constants
import { getService } from 'constants/ajax';
import { TRANSPORTATION } from 'constants/transportation';
import { CHECKED_IN } from 'constants/selfCheckIn';
import { VEHICLE_VALUES } from 'constants/vehicle';

const { VALET, VALETLOANER } = TRANSPORTATION;

export const setSelfCheckIn = createAction(actionTypes.SET_SELF_CHECK_IN_DATA);

export const getSelfCheckInDetails = (
  config: Object
): Thunk<Promise<any>> => async (dispatch, getState) => {
  const webkey = getWebkey(getState());
  const appointmentDetailsService = getService('selfCheckIn');
  appointmentDetailsService.setPathVars({ webkey });
  appointmentDetailsService.setCallBacks(dispatch(ajaxCallbacks()));
  try {
    const { data } = await xws(appointmentDetailsService).get(
      appointmentDetailsService.getPath(),
      {
        params: {
          ...config,
          tokenId: getTokenId(getState())
        }
      }
    );
    return data;
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
    /* eslint-enable no-console */
    return false;
  }
};

export const setAppoinment = (appointment: Object) => async (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  const contactInfo = appointment.personInfo;
  const startTimeFormatted = parseDate(`${appointment.startTime}T00:00:00`);
  const { id: personId } = contactInfo;
  await dispatch(actions.setPersonInfo(contactInfo));
  dispatch(setPersonId(personId));
  const { userRegDate, ...vehicleData } = appointment.vehicle;
  const regDate = parseDate(`${userRegDate}T00:00:00`);
  const vehicle = !isNaN(new Date(regDate).getTime())
    ? {
        vehRegDate: formatDate(regDate, 'MM/DD/YYYY'),
        regDate: userRegDate,
        ...vehicleData,
        personId
      }
    : { ...vehicleData, personId };
  const existMake = findMake(getState())(vehicle.make);
  const { OTHER } = VEHICLE_VALUES;
  const shapedVehicle = existMake
    ? customerVehicleShape(vehicle)
    : customerVehicleShape({
        ...vehicle,
        make: OTHER,
        model: OTHER
      });
  dispatch(setModelLabelOther(vehicle.model));
  dispatch(setMakeLabelOther(vehicle.make));
  dispatch({
    type: SET_CUSTOMER_VEHICLE,
    payload: shapedVehicle
  });
  await dispatch(getTrims(shapedVehicle.trim));
  const services = appointment.maintenanceServices.concat(
    appointment.repairServices
  );
  if (appointment.participantComments) {
    dispatch(setAppointmentNotes(appointment.participantComments));
  }
  const formattedServices = dispatch(formatServices(services));
  dispatch(
    setServices(formattedServices.concat(appointment.autoRecallServices))
  );
  dispatch(setApptServicePoint(appointment.servicePoint));
  await dispatch(updatePackageInfo());
  if (appointment.promoId) {
    const promoList = await dispatch(
      getPromoList(vehicleData.vin, appointment.promoId)
    );
    if (promoList && promoList[0]) {
      dispatch(setActivePromotion(promoList[0]));
    } else {
      dispatch(getAnonymousPromotion(appointment.promoId));
    }
  }
  const formattedDeclinedServices = dispatch(
    formatDeclinedServices(appointment.declinedServices)
  );
  dispatch(setDeclinedServices(formattedDeclinedServices));
  dispatch(setAutoRecallServices(appointment.autoRecallServices));
  dispatch(setAdvisor(appointment.agentId));
  dispatch(setAdvisorName(appointment.agentName));
  appointment.teamId && dispatch(setTeamIds(appointment.teamId.toString()));
  dispatch(setStartTime(startTimeFormatted));
  dispatch(setTransportationOption(appointment.transportationOption));
  dispatch(setTransportationLabel(appointment.transportationDisplayText));
  if (
    appointment.valetDetails &&
    (appointment.transportationOption === VALET ||
      appointment.transportationOption === VALETLOANER)
  ) {
    dispatch(setValetDetails(appointment.valetDetails));
  }
  dispatch(setReservationId(appointment.reservationId));
  dispatch(setSCIReservationId(appointment.reservationId));
  dispatch(setAttendanceStatus(appointment.attendanceStatus));
  dispatch(setConfirmationKey(appointment.confirmationKey));
  dispatch(setCallBack(appointment.callBack));
  dispatch(setLifecycleState(appointment.lifecycleState));
  if (appointment.lead) {
    dispatch(setLeadAppt(appointment.lead));
  }
};

export const checkIn = (waitStatus?: string): Thunk<Promise<any>> => async (
  dispatch,
  getState
) => {
  const {
    checkIn: {
      isNavigating,
      statusTrackerURL,
      selfCheckInStatus,
      ...selfCheckInData
    },
    configFlow: { additionalServices, additionalServicesNotes }
  } = getSelfCheckInData(getState());
  const tellUsMoreSvcId = getTellusmoreSvcId(getState());
  const tellUsMoreNotes = getTellUsMoreNotes(getState());
  const startTime = getStartTime(getState());
  let appointmentNotes = '';
  const hasAdditionalServices =
    (additionalServices || additionalServicesNotes).length > 0;
  if (hasAdditionalServices) {
    if (tellUsMoreNotes) {
      appointmentNotes += `Customer notes: ${tellUsMoreNotes}; `;
    }
    if (additionalServices) {
      const formattedServices = additionalServices.replace(
        /id: [0-9]*, (name|notes): /g,
        ''
      );
      appointmentNotes += `SCI Services: ${formattedServices}`;
    }
    if (additionalServicesNotes) {
      appointmentNotes += `SCI Notes: ${additionalServicesNotes}`;
    }
    if (tellUsMoreSvcId && appointmentNotes) {
      const tellUsMoreService = {
        id: tellUsMoreSvcId,
        notes: appointmentNotes,
        name: appointmentNotes,
        price: 0
      };
      if (tellUsMoreNotes) {
        await dispatch(updateTellusmoreService(tellUsMoreService));
      } else {
        await dispatch(addService(tellUsMoreService));
        dispatch(setStartTime(startTime));
      }
    }
  }

  const bookServiceData = await dispatch(constructBookServiceData());
  const appointment = JSON.stringify(bookServiceData);

  const checkIn = getService('checkIn');
  const webkey = getWebkey(getState());
  const locale = getLocaleCode(getState());
  const tokenId = getTokenId(getState());
  checkIn.setPathVars({ webkey });
  checkIn.setCallBacks(dispatch(ajaxCallbacks()));

  const checkInData = waitStatus
    ? {
        ...selfCheckInData,
        selfCheckinStatus: CHECKED_IN,
        selfCheckinWaitState: waitStatus,
        hasAdditionalServices,
        appointment,
        locale,
        tokenId
      }
    : {
        ...selfCheckInData,
        selfCheckinStatus: CHECKED_IN,
        hasAdditionalServices,
        appointment,
        locale,
        tokenId
      };
  /* eslint-disable no-console */
  console.log(checkInData);
  /* eslint-enable no-console */
  try {
    const request = xws(checkIn).post(checkIn.getPath(), checkInData);
    const {
      data: { success }
    } = await request;

    const isDealerOpenNow = getIsDealerOpenNow(getState());

    dispatch(
      gtmUserEvent(
        `Complete check in button`,
        'Checked-in',
        `Self Check-in During${isDealerOpenNow ? '' : ' Non'} Operation Hours`,
        'GTM_CURRENT_PAGE > Complete check-in'
      )
    );

    if (success && success.data && success.data.startTime) {
      const startTimeFormatted = parseDate(
        `${success.data.startTime}T00:00:00`
      );
      setStartTime(startTimeFormatted);
    }
    return success;
  } catch (e) {
    // @TODO: error handling
    /* eslint-disable no-console */
    console.log(e);
    return false;
    /* eslint-enable no-console */
  }
};

export const setSelfCheckInData = (selfCheckInData: Object) => {
  return {
    type: actionTypes.SET_SELF_CHECK_IN_DATA,
    payload: selfCheckInData
  };
};

export const setSCIReservationId = (reservationId: number) => {
  return {
    type: actionTypes.SET_SCI_RESERVATION_ID,
    payload: reservationId
  };
};

export function setSignature(signature: string | null) {
  return {
    type: actionTypes.SET_SIGNATURE,
    payload: signature
  };
}

export function setAppointmentNotes(appointmentNotes: string | null) {
  return {
    type: actionTypes.SET_APPOINTMENT_NOTES,
    payload: appointmentNotes
  };
}

export const setWaitState = (waitState: string) => {
  return {
    type: actionTypes.SET_SCI_WAIT_STATE,
    payload: waitState
  };
};

export const setMileage = (
  mileage: number
): Thunk<Promise<any>> => async dispatch => {
  dispatch(setSCIMileage(+mileage));
  dispatch(setApptMileage(+mileage));
};

const setSCIMileage = (mileage: number) => {
  return {
    type: actionTypes.SET_SCI_MILEAGE,
    payload: mileage
  };
};

const setApptMileage = (mileage: number) => {
  return {
    type: SET_MILEAGE,
    payload: mileage
  };
};

export const setLicenseNumber = (license: string) => {
  return {
    type: actionTypes.SET_SCI_LICENSE_NUMBER,
    payload: license
  };
};

export const setCarColor = (color: string) => {
  return {
    type: actionTypes.SET_SCI_CAR_COLOR,
    payload: color
  };
};

export const setCustomerMobileNumber = (mobileNumber: string) => (
  dispatch: Dispatch
) =>
  dispatch({
    type: actionTypes.SET_SCI_MOBILE_NUMBER,
    payload: dispatch(getPhoneString(mobileNumber))
  });

export const setSelfCheckInSigned = (signed: string) => {
  return {
    type: actionTypes.SET_SCI_SIGNED,
    payload: signed
  };
};

export const setPersonId = (personId: number) => {
  return {
    type: actionTypes.SET_SCI_PERSON_ID,
    payload: personId
  };
};

export const setSelfCheckInStatus = (selfCheckInStatus: string) => {
  return {
    type: actionTypes.SET_SELF_CHECKIN_STATUS,
    payload: selfCheckInStatus
  };
};

export const setStatusTrackerURL = (statusTrackerURL: string) => {
  return {
    type: actionTypes.SET_STATUS_TRACKER_URL,
    payload: statusTrackerURL
  };
};

export const setIsSciNavigating = (isNavigating: boolean) => {
  return {
    type: actionTypes.SET_SCI_IS_NAVIGATING,
    payload: isNavigating
  };
};

export const updateAdditionalServices = (service: string) => {
  return {
    type: actionTypes.UPDATE_SCI_ADDITIONAL_SERVICES,
    payload: service
  };
};

export const updateAdditionalServicesNotes = (notes: string) => {
  return {
    type: actionTypes.UPDATE_SCI_ADDITIONAL_SERVICES_NOTES,
    payload: notes
  };
};

export const setSelfCheckInFlow = (selfCheckInFlow: string) => {
  return {
    type: actionTypes.SET_SELF_CHECK_IN_FLOW,
    payload: selfCheckInFlow
  };
};

export const setIsDealerOpenNow = (isDealerOpenNow: boolean) => {
  return {
    type: actionTypes.SET_IS_DEALER_OPEN_NOW,
    payload: isDealerOpenNow
  };
};

export const setTellUsMoreSvcId = (tellUsMoreSvcId: number) => {
  return {
    type: actionTypes.SET_SCI_TELL_US_MORE_ID,
    payload: tellUsMoreSvcId
  };
};

export const setInitialLoadingComplete = (status: boolean) => {
  return {
    type: actionTypes.SET_INITIAL_LOADING_COMPLETE,
    payload: status
  };
};

const getPhoneString = (mobilePhone: ?string) => (
  dispatch: Dispatch,
  getState: () => StoreState
) => {
  return rawPhoneNumber(
    formatPhoneNumber(mobilePhone, getDealerCountryCode(getState()))
  );
};



// WEBPACK FOOTER //
// ./src/store/reducers/selfCheckIn/actions.js